import { useState, useCallback } from 'react';
import { permissionDemoApi } from '../api/permissionDemoApi';
import type { DemoLevel, DemoEndpointResult } from '../types/permissionDemo';

/**
 * Manages manual (button-triggered) calls to the 4 demo endpoints.
 * NOT a useQuery — calls are imperative (user clicks "호출").
 * Returns per-endpoint result state and a trigger function.
 *
 * 403 = expected demo result → status: 'forbidden' (NOT error red).
 */
export function useDemoEndpoint() {
  const [results, setResults] = useState<Record<DemoLevel, DemoEndpointResult>>({
    public: { status: 'idle' },
    'user-only': { status: 'idle' },
    'manager-only': { status: 'idle' },
    'admin-only': { status: 'idle' },
  });

  const call = useCallback(async (level: DemoLevel) => {
    setResults((prev) => ({ ...prev, [level]: { status: 'loading' } }));
    try {
      const data = await permissionDemoApi.callDemoEndpoint(level);
      setResults((prev) => ({ ...prev, [level]: { status: 'ok', data } }));
    } catch (err) {
      const e = err as { code?: string; message?: string; detail?: string; statusCode?: number };
      // 403 is a normal demo result — surface as 'forbidden', not 'error'
      if (e.statusCode === 403 || e.code?.startsWith('AUTH-403')) {
        setResults((prev) => ({
          ...prev,
          [level]: {
            status: 'forbidden',
            code: e.code ?? 'AUTH-403-001',
            message: e.message ?? '권한이 부족합니다',
            detail: e.detail,
          },
        }));
      } else {
        setResults((prev) => ({
          ...prev,
          [level]: { status: 'error', message: e.message ?? '알 수 없는 오류' },
        }));
      }
    }
  }, []);

  const reset = useCallback((level: DemoLevel) => {
    setResults((prev) => ({ ...prev, [level]: { status: 'idle' } }));
  }, []);

  const resetAll = useCallback(() => {
    setResults({
      public: { status: 'idle' },
      'user-only': { status: 'idle' },
      'manager-only': { status: 'idle' },
      'admin-only': { status: 'idle' },
    });
  }, []);

  return { results, call, reset, resetAll };
}
